'use client';

import { ChevronDown } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { BaseLineCheck2 } from '@/app/components/icons/BaseLineCheck2';
import { BaseLineLanguage } from '@/app/components/icons/BaseLineLanguage';
import { BaseLineLoading1 } from '@/app/components/icons/BaseLineLoading1';
import { BaseLineProfile } from '@/app/components/icons/BaseLineProfile';
import { UserAvatar } from '@/app/components/shared/UserAvatar';
import { useSignOut } from '@/app/components/shared/UserCenter/use-sign-out';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import { GLOBAL_LANGUAGES_CONFIG, SITE } from '@/app/constants';
import { useDevice } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { SupportedLanguage } from '@/app/types';
import { cn, setRegisterPage } from '@/app/utils';
import { safeStorage } from '@/app/utils/client';
import { useLocale, usePathname, useRouter } from '@/i18n/routing';
import { StreamLoginDialog } from './StreamLoginDialog';

export const StreamUserMenu = ({
  className,
  onBeforeLeave,
}: {
  className?: string;
  onBeforeLeave?: (action: () => void) => void;
}) => {
  const { userInfo, isUserInfoInitialized } = useStore((state) => ({
    userInfo: state.userInfo,
    isUserInfoInitialized: state.isUserInfoInitialized,
  }));
  const t = useTranslations();
  const router = useRouter();
  const locale = useLocale();
  const pathname = usePathname();
  const signOut = useSignOut();
  const { isMobile } = useDevice();
  const [loginOpen, setLoginOpen] = useState(false);
  const [mobileLanguageOpen, setMobileLanguageOpen] = useState(false);

  const handleLanguageChange = (language: SupportedLanguage) => {
    router.replace(pathname, { locale: language });
    setRegisterPage({ language });
  };

  if (!isUserInfoInitialized) {
    return (
      <div className={`flex size-9 items-center justify-center ${className ?? ''}`} aria-label="Loading">
        <BaseLineLoading1 aria-hidden="true" className="size-5 animate-spin text-system-white64" />
      </div>
    );
  }

  if (!userInfo) {
    return (
      <div>
        <button
          type="button"
          className="rounded-8 border border-system-white40 px-4 py-2 text-sm text-white transition-colors hover:bg-system-white08"
          onClick={() => setLoginOpen(true)}
        >
          {t('header.navLogin')}
        </button>
        <StreamLoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} />
      </div>
    );
  }

  return (
    <div className={`flex items-center ${className ?? ''}`}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Open profile menu"
            className="flex items-center justify-center rounded-full outline-none ring-white/40 focus-visible:ring-2"
          >
            <UserAvatar userInfo={userInfo} className="size-9 cursor-pointer" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={8}
          className="w-[240px] select-none overflow-visible rounded-12 border border-system-white12 bg-system-bg02 p-2 [box-shadow:0px_20px_60px_0px_rgba(0,0,0,0.32)] sm:max-h-[calc(100dvh-24px)] sm:overflow-y-auto"
        >
          <DropdownMenuItem
            className="flex cursor-pointer items-center gap-2 rounded-8 px-2 py-3 text-sm text-white leading-6"
            onClick={() => {
              const navigateToProfile = () => router.push('/vidu-stream/profile');
              if (onBeforeLeave) {
                onBeforeLeave(navigateToProfile);
              } else {
                navigateToProfile();
              }
            }}
          >
            <BaseLineProfile className="text-lg" />
            {t('viduStreamNavigation.me')}
          </DropdownMenuItem>
          {SITE === 'global' && isMobile ? (
            <div>
              <DropdownMenuItem
                className="flex cursor-pointer items-center gap-2 rounded-8 px-2 py-3 text-sm text-white leading-6 hover:bg-system-hover01 focus:bg-system-hover01"
                onSelect={(event) => {
                  event.preventDefault();
                  setMobileLanguageOpen((open) => !open);
                }}
              >
                <BaseLineLanguage className="flex-shrink-0 text-xl" />
                {t('sidebar.systemItems.language')}
                <ChevronDown
                  className={cn('ml-auto size-4 transition-transform', mobileLanguageOpen && 'rotate-180')}
                />
              </DropdownMenuItem>
              {mobileLanguageOpen ? (
                <div className="mx-1 mb-1 rounded-8 bg-system-white04 p-1">
                  {GLOBAL_LANGUAGES_CONFIG.map(({ label, value }) => (
                    <DropdownMenuItem
                      key={value}
                      className="flex h-10 items-center justify-between whitespace-nowrap rounded-8 px-2 text-sm text-white leading-6 hover:bg-system-hover01 focus:bg-system-hover01"
                      onClick={() => handleLanguageChange(value)}
                    >
                      {label}
                      {locale === value ? <BaseLineCheck2 className="text-lg" /> : null}
                    </DropdownMenuItem>
                  ))}
                </div>
              ) : null}
            </div>
          ) : SITE === 'global' ? (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="gap-2 rounded-8 px-2 py-3 text-sm text-white leading-6 hover:bg-system-hover01 focus:bg-system-hover01 data-[state=open]:bg-system-hover01">
                <BaseLineLanguage className="flex-shrink-0 text-xl" />
                {t('sidebar.systemItems.language')}
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent
                  sideOffset={12}
                  collisionPadding={8}
                  className="max-h-[calc(100dvh-16px)] w-[220px] overflow-y-auto rounded-12 border border-system-white12 bg-system-bg02 p-4 [box-shadow:0px_20px_60px_0px_rgba(0,0,0,0.32)] sm:w-[168px] sm:p-2"
                >
                  {GLOBAL_LANGUAGES_CONFIG.map(({ label, value }) => (
                    <DropdownMenuItem
                      key={value}
                      className="flex h-10 items-center justify-between whitespace-nowrap rounded-8 px-2 text-sm text-white leading-6 hover:bg-system-hover01 focus:bg-system-hover01"
                      onClick={() => handleLanguageChange(value)}
                    >
                      {label}
                      {locale === value ? <BaseLineCheck2 className="text-lg" /> : null}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
          ) : null}
          <DropdownMenuSeparator className="my-1" />
          <DropdownMenuItem
            className="cursor-pointer rounded-8 px-3 py-2.5 text-sm text-system-white64 hover:bg-[rgba(255,48,48,0.12)] hover:text-white"
            onClick={() => {
              const signOutAction = () => {
                safeStorage.removeItem('utm_term');
                signOut();
              };
              if (onBeforeLeave) {
                onBeforeLeave(signOutAction);
              } else {
                signOutAction();
              }
            }}
          >
            {t('personal.logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
